"use client";

import React from "react";
import { Phone, MapPin, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 bg-zinc-800/50 border border-zinc-700 rounded-xl animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
        <p className="text-zinc-400 max-w-md mx-auto mb-8">
          {successMessage || "Thank you for your interest. We will review your project details and get back to you shortly with an estimate."}
        </p>
        <Button variant="outline" className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 hover:text-white" onClick={() => window.location.reload()}>
          Start Another Request
        </Button>
      </div>;
  }
  const inputStyles = "rounded-none border-0 border-b-2 border-zinc-700 bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors duration-300 placeholder:text-zinc-600 text-zinc-100 h-auto";
  const labelStyles = "text-zinc-400 font-medium uppercase tracking-wider text-xs";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="project_type" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Project Type</FormLabel>
            <FormControl>
              <select className={`${inputStyles} w-full cursor-pointer appearance-none`} {...field} value={field.value || ""}>
                <option value="" disabled className="bg-zinc-900 text-zinc-500">Select a project type</option>
                <option value="Driveway" className="bg-zinc-900 text-zinc-100">Driveway</option>
                <option value="Patio" className="bg-zinc-900 text-zinc-100">Patio</option>
                <option value="Stamped / Decorative Concrete" className="bg-zinc-900 text-zinc-100">Stamped / Decorative Concrete</option>
                <option value="Foundation / Slab" className="bg-zinc-900 text-zinc-100">Foundation / Slab</option>
                <option value="Retaining Wall" className="bg-zinc-900 text-zinc-100">Retaining Wall</option>
                <option value="Other" className="bg-zinc-900 text-zinc-100">Other</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Project Address (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, Indianapolis, IN" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Brief description of your project, size, any special requirements..." className={`${inputStyles} min-h-[100px] resize-y`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-900/20 border border-red-900/50 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-red-200 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm shadow-[0_0_20px_rgba(234,179,8,0.3)] hover:shadow-[0_0_30px_rgba(234,179,8,0.5)] transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : "Get Free Estimate"}
      </Button>
      
      <p className="text-center text-zinc-500 text-xs mt-4">
        By submitting this form, you agree to be contacted about your project. Your information is kept private.
      </p>
    </Form>;
}
export default function StartYourProject() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return;
}